import React, { useState, useEffect, useRef } from "react";
import "../../css/GenerateIdCards.css";
import { Search, Upload, Download, Printer, RotateCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function GenerateIdCards() {
  const [activeTab, setActiveTab] = useState("student");
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showFrontSide, setShowFrontSide] = useState(true);
  const idCardRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    if (activeTab === "student") {
      await fetchStudents();
    } else {
      await fetchTeachers();
    }
  };

  const fetchStudents = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch students");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeachers = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/teachers", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch teachers");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching teachers:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowFrontSide(true);
    const uniqueId =
      activeTab === "student"
        ? `STD${user.rollNumber.padStart(6, "0")}${user._id.slice(-4)}`
        : `TCH${user.employeeId.padStart(6, "0")}${user._id.slice(-4)}`;

    setQrCodeValue(uniqueId);
    setProfileImage(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getAdmissionYear = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const uploadIDCardToBackblaze = async (imageData) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return null;
    }

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/idcard/upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ imageData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload ID card");
      }

      const result = await response.json();
      return result.url;
    } catch (error) {
      console.error("Error uploading ID card:", error);
      toast.error(`Error uploading ID card: ${error.message}`);
      return null;
    }
  };

  const captureAndProcessCard = async () => {
    if (!idCardRef.current || !selectedUser) return null;

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(idCardRef.current, {
        scale: 3,
        logging: false,
        useCORS: true,
        backgroundColor: "#ffffff",
        allowTaint: true,
        height: idCardRef.current.offsetHeight + 10,
        windowHeight: idCardRef.current.offsetHeight + 50,
        onclone: (clonedDoc) => {
          const clonedQrCode = clonedDoc.querySelector('.id-card-qr-code svg');
          if (clonedQrCode) {
            clonedQrCode.style.display = 'inline-block';
            clonedQrCode.style.width = '250px';
            clonedQrCode.style.height = '250px';
          }
        }
      });

      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error capturing card:", error);
      toast.error(`Error capturing card: ${error.message}`);
      return null;
    }
  };

  const saveIDCard = async () => {
    if (!selectedUser) return;

    setIsUploading(true);
    try {
      setShowFrontSide(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      const frontImageData = await captureAndProcessCard();
      setShowFrontSide(false);
      await new Promise(resolve => setTimeout(resolve, 300));
      const backImageData = await captureAndProcessCard();

      if (!frontImageData || !backImageData) {
        throw new Error("Failed to capture ID card images");
      }
      const frontImageUrl = await uploadIDCardToBackblaze(frontImageData);
      const backImageUrl = await uploadIDCardToBackblaze(backImageData);

      if (!frontImageUrl || !backImageUrl) {
        throw new Error("Failed to upload ID card images");
      }
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://schoolpro-api.uniums.com/api/idcard/${activeTab}/update-barcode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: selectedUser._id,
            barcodeValue: qrCodeValue,
            idCardImageUrl: frontImageUrl,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save QR code");
      }
      toast.success(`ID Card for ${selectedUser.firstName} ${selectedUser.lastName} saved successfully!`);
      downloadImage(frontImageData, `IDCard_Front_${selectedUser.firstName}_${selectedUser.lastName}.png`);
      downloadImage(backImageData, `IDCard_Back_${selectedUser.firstName}_${selectedUser.lastName}.png`);
      setShowFrontSide(true);

    } catch (error) {
      console.error("Error saving ID card:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };
  const downloadImage = (dataUrl, filename) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    link.click();
  };

  const downloadIdCard = async () => {
    await saveIDCard();
  };

  const handlePrint = async () => {
    if (!selectedUser) return;
    setIsUploading(true);
    try {
      setShowFrontSide(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      const frontImageData = await captureAndProcessCard();

      setShowFrontSide(false);
      await new Promise(resolve => setTimeout(resolve, 300));
      const backImageData = await captureAndProcessCard();

      if (!frontImageData || !backImageData) {
        throw new Error("Failed to capture ID card images");
      }
      const frontImageUrl = await uploadIDCardToBackblaze(frontImageData);
      const backImageUrl = await uploadIDCardToBackblaze(backImageData);

      if (!frontImageUrl || !backImageUrl) {
        throw new Error("Failed to upload ID card images");
      }
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://schoolpro-api.uniums.com/api/idcard/${activeTab}/update-barcode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: selectedUser._id,
            barcodeValue: qrCodeValue,
            idCardImageUrl: frontImageUrl,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save QR code");
      }
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>ID Card - ${selectedUser.firstName} ${selectedUser.lastName}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .print-container { display: flex; flex-direction: column; gap: 30px; }
              .card-side { max-width: 350px; border: 1px dashed #ccc; padding: 10px; margin: 0 auto; }
              h3 { text-align: center; color: #666; }
              img { width: 100%; height: auto; }
            </style>
          </head>
          <body>
            <div class="print-container">
              <div class="card-side">
                <h3>Front Side</h3>
                <img src="${frontImageData}" alt="ID Card Front" />
              </div>
              <div class="card-side">
                <h3>Back Side</h3>
                <img src="${backImageData}" alt="ID Card Back" />
              </div>
            </div>
            <script>
              window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); }
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();

      toast.success("ID Card saved to database and sent to printer");
      setShowFrontSide(true);
    } catch (error) {
      console.error("Error processing print request:", error);
      toast.error(`Could not process print request: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const getFullName = (user) => {
    return `${user.firstName} ${user.lastName}`;
  };

  const filteredUsers = users.filter((user) => {
    const searchFields =
      activeTab === "student"
        ? [user.firstName, user.lastName, user.rollNumber]
        : [user.firstName, user.lastName, user.employeeId];

    return searchFields.some(
      (field) => field && field.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const flipCard = () => {
    setShowFrontSide(!showFrontSide);
  };

  return (
    <div className="generate-idCard-container">
      <h1 className="generate-idCard-title">Generate ID Cards</h1>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />

      <div className="generate-idCard-tabs">
        <button
          className={`generate-idCard-tab ${activeTab === "student" ? "active" : ""
            }`}
          onClick={() => setActiveTab("student")}
        >
          Students
        </button>
        <button
          className={`generate-idCard-tab ${activeTab === "teacher" ? "active" : ""
            }`}
          onClick={() => setActiveTab("teacher")}
        >
          Teachers
        </button>
      </div>

      <div className="generate-idCard-content">

        <div className="generate-idCard-search-section">
          <div className="generate-idCard-search">
            <Search size={20} />
            <input
              type="text"
              placeholder={
                activeTab === "student"
                  ? "Search by name or roll number..."
                  : "Search by name or employee ID..."
              }
              value={searchTerm}
              onChange={handleSearch}
              className="generate-idCard-search-input"
            />
          </div>
        </div>
        <div className="generate-idCard-split-view">
          <div className="generate-idCard-users-list">
            <h3 className="generate-idCard-section-title">
              {activeTab === "student" ? "Students" : "Teachers"} List
            </h3>
            <div className="generate-idCard-users">
              {isLoading ? (
                <>
                  <Loader />
                  <p className="generate-idCard-loading">Loading...</p>
                </>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className={`generate-idCard-user-item ${selectedUser && selectedUser._id === user._id
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => handleUserSelect(user)}
                  >
                    <h4>{getFullName(user)}</h4>
                    <p>
                      {activeTab === "student"
                        ? `Roll: ${user.rollNumber}`
                        : `ID: ${user.employeeId}`}
                    </p>
                  </div>
                ))
              ) : (
                <p className="generate-idCard-no-results">
                  No {activeTab}s found
                </p>
              )}
            </div>
          </div>
          <div className="generate-idCard-preview">
            <h3 className="generate-idCard-section-title">
              ID Card Preview
              {selectedUser && (
                <span className="id-card-side-indicator">
                  {showFrontSide ? "(Front Side)" : "(Back Side)"}
                </span>
              )}
            </h3>

            {selectedUser ? (
              <>
                <div className="generate-idCard-card-wrapper">
                  {showFrontSide ? (
                    <div ref={idCardRef} className="id-card">

                      <div className="id-card-header">
                        <div className="id-card-logo-section">
                          <div className="id-card-school-logo"></div>
                          <div className="id-card-school-name-container">
                            <h2 className="id-card-school-name">UniUMS School Pro</h2>
                            <p className="id-card-school-slogan">Excellence in Education</p>
                          </div>
                        </div>
                      </div>

                      <div className="id-card-content">
                        <div className="id-card-photo-container">
                          {profileImage ? (
                            <img
                              src={profileImage}
                              alt="Profile"
                              className="id-card-profile-image"
                            />
                          ) : (
                            <div className="id-card-photo-placeholder">
                              <p>Photo</p>
                            </div>
                          )}
                        </div>

                        <div className="id-card-details">
                          <h2 className="id-card-name">{getFullName(selectedUser)}</h2>

                          <div className="id-card-badge">
                            {activeTab === "student" ?
                              `Student • Grade ${selectedUser.grade}-${selectedUser.section}` :
                              selectedUser.position || "Teacher"}
                          </div>

                          <div className="id-card-info-container">
                            <div className="id-card-info-item">
                              <div className="id-card-info-label">ID</div>
                              <div className="id-card-info-value">
                                {activeTab === "student" ? selectedUser.rollNumber : selectedUser.employeeId}
                              </div>
                            </div>

                            <div className="id-card-info-item">
                              <div className="id-card-info-label">Blood</div>
                              <div className="id-card-info-value">{selectedUser.bloodGroup || "N/A"}</div>
                            </div>


                            {activeTab === "student" ? (
                              <div className="id-card-info-item">
                                <div className="id-card-info-label">Admission</div>
                                <div className="id-card-info-value">
                                  {getAdmissionYear(selectedUser.admissionDate) || "N/A"}
                                </div>
                              </div>
                            ) : (
                              <div className="id-card-info-item">
                                <div className="id-card-info-label">Joined</div>
                                <div className="id-card-info-value">
                                  {getAdmissionYear(selectedUser.joiningDate) || "N/A"}
                                </div>
                              </div>
                            )}

                            <div className="id-card-info-item">
                              <div className="id-card-info-label">Phone</div>
                              <div className="id-card-info-value">{selectedUser.phone}</div>
                            </div>
                          </div>
                          <div className="id-card-back-address">
                            <h4>Address</h4>
                            <p>{selectedUser.address || "N/A"}</p>
                          </div>

                          <div className="id-card-footer">
                            <div className="id-card-valid">Valid Until: Apr 2026</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div ref={idCardRef} className="id-card id-card-back">

                      <div className="id-card-back-header">
                        <h3>Student Identification Card</h3>
                      </div>

                      <div className="id-card-back-content">
                        <div className="id-card-qr-code">
                          {qrCodeValue && (
                            <QRCodeSVG
                              value={qrCodeValue}
                              size={250}
                              level="H"
                              includeMargin={true}
                            />
                          )}
                        </div>



                        <div className="id-card-back-address">
                          <h4>Address</h4>
                          <p>{selectedUser.address || "N/A"}</p>
                        </div>

                        <div className="id-card-back-notice">
                          <p>If found, please return to:</p>
                          <p className="id-card-back-school">UniUMS School Pro</p>
                          <p>123 Education Lane, Knowledge City</p>
                          <p>Email: info@uniumpschool.edu</p>
                          <p>Phone: (555) 123-4567</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="generate-idCard-controls">
                  <label className="generate-idCard-btn generate-idCard-upload-btn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="generate-idCard-file-input"
                    />
                    <Upload size={16} /> Upload Photo
                  </label>

                  <button
                    className="generate-idCard-btn generate-idCard-flip-btn"
                    onClick={flipCard}
                  >
                    <RotateCw size={16} />
                    {showFrontSide ? "Show Back" : "Show Front"}
                  </button>

                  <button
                    className="generate-idCard-btn generate-idCard-save-btn"
                    onClick={downloadIdCard}
                    disabled={isUploading}
                  >
                    <Download size={16} />
                    {isUploading ? "Processing..." : "Download ID Card"}
                  </button>

                  <button
                    className="generate-idCard-btn generate-idCard-print-btn"
                    onClick={handlePrint}
                    disabled={isUploading}
                  >
                    <Printer size={16} />
                    {isUploading ? "Processing..." : "Print"}
                  </button>
                </div>
              </>
            ) : (
              <div className="generate-idCard-no-selection">
                <p>Select a {activeTab} to generate ID card</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenerateIdCards;